import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Form, InputNumber, notification, Popconfirm, Switch } from 'antd';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid';
import CSVModalGrid from 'components/CSVUploadGrid';
import CrossTable from 'components/CrossTable';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useQuery } from 'react-query';
import {
  ParAssetCatalogResidualValue,
  getAssetCatalogRVByCatalogID,
  addAssetCatalogRV,
  updateAssetCatalogRV,
  deleteAssetCatalogRV,
  GetAssetCatalogRVByCatalogIDParams,
  AssetCatalogResponse,
} from 'services/assetconfig';

import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import UpdateButton from 'components/UpdateButtonAction';
import DeleteButton from 'components/DeleteButton';
import { useFormList } from 'hooks/useFormList';
import { indexBy, shouldUpdateCompare } from 'utils/utils2';
import { isEqual, isNil } from 'lodash';
import ProductInput from 'pages/ProposalV3/PropDetail/ProductInput';
import FormInput from 'components/FormInput';
import InputRate from 'components/InputRate';
// import InputMoney from 'components/InputMoney';
import SaveButton from 'components/SaveButtonAction';
import ButtonComponent from 'components/Button';
import InputCurrency from 'components/InputCurrency';
import MgUserJobAuthorizationModal from 'components/MgUserJobAuthorizationModal';
import ModalControllerNV from 'components/ModalControllerNV';
import { AuthContext } from 'components/Auth/Auth';
import PricingListFilteredSelect from 'components/PricingListFilteredSelect';

export type RVProps = {
  id: number;
  asset: AssetCatalogResponse;
  isModal?: string;
};
const NEW_RV_ID = -1;

const AssetCatalogRVList = (props: RVProps) => {
  const { isInspect } = useContext(AuthContext);
  const { id, isModal } = props;
  const { catalogid , currcode} = props.asset;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const fieldName = 'RV';
  const [searchs, setsearchs] = useState(false);
  const [isFromCSV, setIsFromCSV] = useState(false); // Flag for CSVModalGrid context
  const [viewMode, setViewMode] = useState<'grid' | 'crossTable'>('grid'); // New state for toggling views

  const initialSearchParams: GetAssetCatalogRVByCatalogIDParams = {
    catalogid: id,
    prcode: '',
    rvtype: '',
  };
  const [searchParams, setSearchParams] =
    useState<GetAssetCatalogRVByCatalogIDParams>(initialSearchParams);
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'rvid',
    addAsFirst: true,
  });

  type Values = {
    RVs: ParAssetCatalogResidualValue[];
  };
  const { data, refetch } = useQuery(
    ['getAssetCatalogRVByCatalogID', searchParams],
    () =>
      getAssetCatalogRVByCatalogID({
        catalogid: searchParams.catalogid,
        rvtype: searchParams.rvtype,
        prcode: searchParams.prcode,
      })
        .then((res) => res.list || [])
        .catch((err) => []),
    { enabled: !isNil(searchParams.catalogid) && searchs }
  );

  const onSaveOrEdit = useCallback(
    (record: ParAssetCatalogResidualValue, fromCSV = false) => {
      const currentValue = fromCSV
        ? { ...record, catalogid: id } // Use record as-is for CSV data
        : {
          ...form.getFieldValue(fieldName)[indexBy(form.getFieldValue(fieldName), 'rvid')[record.rvid]],
          catalogid: id,
          exceedformula:
            typeof form.getFieldValue(fieldName)[indexBy(form.getFieldValue(fieldName), 'rvid')[record.rvid]].exceedformula === "object"
              ? (form.getFieldValue(fieldName)[indexBy(form.getFieldValue(fieldName), 'rvid')[record.rvid]].exceedformula as any)?.priid
              : form.getFieldValue(fieldName)[indexBy(form.getFieldValue(fieldName), 'rvid')[record.rvid]].exceedformula,
        };

      const { rvid } = currentValue;
      const saveOrUpdate = rvid === NEW_RV_ID
        ? addAssetCatalogRV({ request: { ...currentValue, rvid: null as any } })
        : updateAssetCatalogRV({ rvid: currentValue.rvid, request: currentValue });

      saveOrUpdate.then((response) => {
        if (response.code === '200') {
          notification.success({ message: response.message });
          refetch();
        }
      });
    },
    [form, id, refetch]
  );

  useEffect(() => {
    if (data) {
      if (!isFromCSV) {
        form.setFieldsValue({ [fieldName]: data });
      }
      setIsFromCSV(false); // Reset flag after setting data
    }
  }, [data, form, isFromCSV]);
  const getName = useCallback(
    (name: string) => (record: ParAssetCatalogResidualValue) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'rvid')[record.rvid];

      return [fieldName, index, name];
    },
    [form]
  );
  const addRow = () => {
    add(row);
  };
  useEffect(() => {
    if (data) {
      form.setFieldsValue({ [fieldName]: data });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  const columns = useMemo<DataGridColDef<ParAssetCatalogResidualValue>[]>(
    () => [
      {
        dataIndex: 'prcode',
        filtrable: true,
        filterComponent: <ProductInput />,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('prcode')(record)}>
              <ProductInput />
            </Form.Item>
          );
        },
        width: 200,
      },
      {
        dataIndex: 'rvtype',
        filtrable: true,
        filterComponent: <ParListSelect parcode="RVTYPE" />,

        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('rvtype')(record)}>
              <ParListSelect parcode="RVTYPE" />
            </Form.Item>
          );
        },
        width: 200,
      },
      {
        dataIndex: 'rvstartmethod',
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('rvstartmethod')(record)}>
              <ParListSelect parcode="RVSTARTM" />
            </Form.Item>
          );
        },
        width: 200,
      },
      {
        dataIndex: 'monthsmin',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('monthsmin')(record)}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} />
            </Form.Item>
          );
        },
        width: 100,
      },
      {
        dataIndex: 'monthsmax',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('monthsmax')(record)}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} />
            </Form.Item>
          );
        },
        width: searchParams.prcode === 'LLD' ? 'hide' : 100,
      },
      {
        dataIndex: 'mileagemin',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('mileagemin')(record)}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} />
            </Form.Item>
          );
        },
        width: 100,
      },
      {
        dataIndex: 'mileagemax',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('mileagemax')(record)}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} />
            </Form.Item>
          );
        },
        width: searchParams.prcode === 'LLD' ? 'hide' : 100,
      },
      {
        dataIndex: 'exceedformula',
        render: (value, record) => {
          return (
            <Form.Item name={getName('exceedformula')(record)}>
              <PricingListFilteredSelect
                allowClear
                params={{
                  offerid: 0,
                  ctetype: 'ASSUSE',
                  prcode: '',
                  entity: '',
                  entityid: 0,
                  tpid: 0,
                }}
              />
            </Form.Item>
          );
        },
        width: 160,
      },

      /* {
        dataIndex: 'monthdecrease',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('monthdecrease')(record)}
              rules={[{ required: true }]}
            >
              <InputRate min={0} />
            </Form.Item>
          );
        },
        width: 100,
      },*/
      {
        dataIndex: 'rvevaluationmode',
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('rvevaluationmode')(record)}>
              <ParListSelect parcode="RVEVALMODE" />
            </Form.Item>
          );
        },
        width: 200,
      },
      {
        dataIndex: 'rvfixedamount',
        render: (value, record, index) => {
          return (
            <Form.Item noStyle shouldUpdate={shouldUpdateCompare(fieldName)}>
              {({ getFieldValue }) => {
                return (
                  <FormInput name={getName('rvfixedamount')(record)} noStyle>
                    <InputCurrency
                      disabled={
                        getFieldValue([
                          fieldName,
                          index,
                          'rvevaluationmode',
                        ]) !== 'FIXED'
                      }
                      currcode={currcode}
                    />
                  </FormInput>
                );
              }}
            </Form.Item>
          );
        },
        width: 100,
      },
      {
        dataIndex: 'extendrules',
        render: (value, record, index) => {
          return (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <MgUserJobAuthorizationModal
                  visible={visible}
                  onCancel={() => {
                    setVisible(false);
                  }}
                  attcode="ASSETRV"
                  mgid={Number(localStorage.getItem('mgid'))}
                  offerId={0}
                  rvid={record.rvid}
                />
              )}
              content={({ setVisible, visible }) => (
                <a
                  onClick={() => {
                    !isInspect && setVisible(true);
                  }}
                  hidden={record.rvid === NEW_RV_ID}
                >
                  {' '}
                  {`extend rules ( ${record?.countrvid} )`}
                </a>
              )}
            />
          );
        },
        width: 130,
      },
      {
        dataIndex: 'rvpercentage',
        render: (value, record, index) => {
          return (
            <Form.Item noStyle shouldUpdate={shouldUpdateCompare(fieldName)}>
              {({ getFieldValue }) => {
                return (
                  <FormInput name={getName('rvpercentage')(record)} noStyle>
                    <InputRate
                      disabled={
                        getFieldValue([
                          fieldName,
                          index,
                          'rvevaluationmode',
                        ]) !== 'PERC'
                      }
                      min={0}
                    />
                  </FormInput>
                );
              }}
            </Form.Item>
          );
        },
        width: 100,
      },
      {
        dataIndex: 'trg',
        render: (value, record, index) => {
          return (
            <Form.Item noStyle shouldUpdate={shouldUpdateCompare(fieldName)}>
              {({ getFieldValue }) => {
                return (
                  <FormInput name={getName('trg')(record)} noStyle>
                    <InputRate
                      disabled={
                        getFieldValue([fieldName, index, 'rvtype']) === 'FIN'
                      }
                      min={0}
                    />
                  </FormInput>
                );
              }}
            </Form.Item>
          );
        },
        width: 100,
      },

      {
        key: 'actions',
        render: (_, _record) =>
          _record.rvid === NEW_RV_ID ? (
            <>
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                }}
                comcode="save"
              />
              <DeleteButton onlyIcon onClick={() => remove(_record.rvid)} />
            </>
          ) : (
            <>
              <UpdateButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                }}
                size="small"
                comcode="update"
              />
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteAssetCatalogRV({
                    rvid: _record.rvid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: 200,
        fixed: 'right',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, refetch,searchParams]
  );

  return (
    <DF.Provider pagcode="rvlist">
      <Form form={form} initialValues={row}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValue: Values, nextValue: Values) => {
            return !isEqual(prevValue, nextValue);
          }}
        >
          {({ getFieldValue }) => {
            const RVs: ParAssetCatalogResidualValue[] = getFieldValue(fieldName);
            const isNewItemInTable = RVs?.find(({ rvid }) => rvid === NEW_RV_ID) !== undefined;
            return (
              <>
                <Switch
                  checked={viewMode === 'grid'}
                  onChange={(checked) => setViewMode(checked ? 'grid' : 'crossTable')}
                  style={{ marginBottom: 20 }}
                  checkedChildren="Grid View"
                  unCheckedChildren="Cross Table View"
                />
                {viewMode === 'grid' ? (
                  <DataGrid
                    rowKey="rvid"
                    dataSource={RVs}
                    columns={columns}
                    onSearch={(values) => {
                      setSearchParams(values);
                      setsearchs(true);
                    }}
                    titleComcode="assetCatalogRVList"
                    pagination={{ pageSize: 50 }}
                    initialSearchValues={initialSearchParams}
                    hideExtraAction={!!isModal}
                    extraActions={
                      <>
                        <ButtonComponent
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={addRow}
                          disabled={isNewItemInTable}
                          comcode="add"
                          hidden={isNil(catalogid)}
                        />
                        <div className="ant-row" style={{ marginLeft: "-9px", marginRight: "-9px", height: "20px" }} >&nbsp;</div>
                        <CSVModalGrid
                          onSaveOrEdit={(record) => {
                            setIsFromCSV(true); // Set flag
                            onSaveOrEdit(record, true); // Skip form handling
                          }}
                          form={form}
                          id={id}
                          fieldName={fieldName}
                          refetch={refetch}
                        />
                      </>
                    }
                  />
                ) : (
                  // Render cross-table here (your custom cross-table component)
                    <CrossTable data={RVs} />
                )}
              </>
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};
const row: ParAssetCatalogResidualValue = {
  catalogid: 0,
  monthsmax: 0,
  monthsmin: 0,
  mileagemax: 0,
  mileagemin: 0,
  exceedformula: 0,
  prcode: '',
  rvevaluationmode: '',
  rvfixedamount: 0,
  rvid: NEW_RV_ID,
  rvpercentage: 0,
  trg: 0,
  rvtype: '',
  monthdecrease: 0,
  rvstartmethod: '',
  countrvid: 0,
};
export default AssetCatalogRVList;
